// pms-core / domain — the RATE PARAMETER SET (R14, PMS-RATE-MODEL-BRIEF §13).
//
// The first object the rate engine needs, ahead of the generator and well ahead
// of the grid. Everything §2 and §4 of the brief call an input lives here, as
// ONE named object rather than eight columns updated in place.
//
// Why a set and not a settings table (§13.2) — the fourth property is the one
// that pays:
//   1. NAMED and saved as a unit    — `2026`, `2027-draft-a`
//   2. COMPARABLE                   — two sets diff into a readable answer
//   3. REPLAYABLE                   — same set + same dates = same grid
//   4. SIMULABLE                    — a draft set applied to a PROJECTION, to
//                                     ask what 2027 looks like at these numbers
//
// Hard Rule 5's derived-data corollary is doing the work here: a parameter set
// is an INPUT RECORD (small, authored, kept); `pms_rate` is a PROJECTION of it
// and may be regenerated freely; a simulation is a SECOND projection that must
// live in its own store. Keeping the three physically separate is what makes
// simulating against live data safe.
//
// Rationale for the whole shape: "The Case for External Parametrization
// (Configuration)" (Lionel, 2016), DesignVault/00-claude-os/references/.

import type { IsoDate, RoomTypeId, Cents } from './types.js';

// ── Seasons ──────────────────────────────────────────────────────────────────

/** The four pricing seasons. Re-cut annually from Éducation nationale zone
 *  dates — a PRICING calendar, carrying no closure information (brief §9). */
export const RATE_SEASONS = ['basse', 'moyenne', 'haute', 'tresHaute'] as const;
export type RateSeason = (typeof RATE_SEASONS)[number];

export const RATE_SEASON_LABELS_FR: Record<RateSeason, string> = {
  basse: 'Basse',
  moyenne: 'Moyenne',
  haute: 'Haute',
  tresHaute: 'Très Haute',
};

/** One dated stretch of the calendar. `from`/`to` are INCLUSIVE — a season runs
 *  over whole civil days, and an exclusive end reads as an off-by-one to every
 *  human who checks it against a printed calendar. */
export interface SeasonPeriod {
  from: IsoDate;
  to: IsoDate;
  season: RateSeason;
}

// ── Length-of-stay ladder ────────────────────────────────────────────────────

/** A rung of the DIRECT-booking ladder, keyed on its threshold in nights.
 *  Direct-channel only: the OTAs carry no length-of-stay discount at all
 *  (operator, 2026-08-20 — §8.3's falsifiable assumption, tested and held). */
export interface LengthOfStayTier {
  /** Applies from this many nights upward, until the next tier's threshold. */
  minNights: number;
  /** Fraction OFF the stay component, 0…1. `0.07` is « −7 % ». */
  discountStay: number;
  /** Fraction OFF the meal component, 0…1. Independent of `discountStay` (R2:
   *  the stay is discounted harder than the meals) and free to be 0 — which is
   *  the operator's leaning, recorded as a SCENARIO, never as a decision. */
  discountMeals: number;
}

// ── Meal prices ──────────────────────────────────────────────────────────────

/** Breakfast + dinner, per person per night. There are TWO of these pairs and
 *  they must never be collapsed into one (§7, as amended by R12):
 *    - `booking` (13/26) prices the OTA grid AND is what the OTA listing shows
 *    - `direct`  (11/24) prices the direct engine's quote
 *  §7's original headline is INVERTED for the direct quote. Both are stated in
 *  the brief precisely so nobody "simplifies" them into one. */
export interface MealPrices {
  /** Petit-déjeuner, per person per night. */
  breakfast: Cents;
  /** Dîner, per person per night. */
  dinner: Cents;
}

/** The three age bands a child's meals are priced by, direct price only
 *  (PMS-PHONE-BOOKING-BRIEF P7, §4.1). 13–17 eats as an adult and an infant
 *  eats free, so neither is a band here: a band with nothing to price would be
 *  a field someone fills in and nothing reads. */
export const CHILD_MEAL_BANDS = ['child_3_5', 'child_6_8', 'child_9_12'] as const;
export type ChildMealBand = (typeof CHILD_MEAL_BANDS)[number];

export const CHILD_MEAL_BAND_LABELS_FR: Record<ChildMealBand, string> = {
  child_3_5: '3-5 ans',
  child_6_8: '6-8 ans',
  child_9_12: '9-12 ans',
};

/** A child's breakfast and dinner per band, in cents (P8). Lives in the rate
 *  profile and is written into the catalogue products bound to each band when
 *  the profile is published — one authority, two readers (§4.2). */
export type ChildMealPrices = Record<ChildMealBand, MealPrices>;

/** What « Définir les prix enfants » starts from: the breakfasts at Vega's
 *  measured child prices (0 € / 5 € / 7 €, BOARD Q6) and every dinner at the
 *  adult direct dinner until the operator sets them (§7 Q2) — a starting point
 *  that undersells nothing. */
export function seedChildMealsDirect(mealsDirect: MealPrices): ChildMealPrices {
  return {
    child_3_5: { breakfast: 0, dinner: mealsDirect.dinner },
    child_6_8: { breakfast: 500, dinner: mealsDirect.dinner },
    child_9_12: { breakfast: 700, dinner: mealsDirect.dinner },
  };
}

/** The shape, as one French sentence or null — the client mirror of
 *  `pms_child_meals_shape_ok` (20261026100100), which refuses the same. */
export function childMealsProblem(v: unknown): string | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return 'Repas enfants : objet attendu.';
  const rec = v as Record<string, unknown>;
  for (const k of Object.keys(rec)) {
    if (!(CHILD_MEAL_BANDS as readonly string[]).includes(k)) return `Repas enfants : tranche inconnue « ${k} ».`;
  }
  for (const band of CHILD_MEAL_BANDS) {
    const pair = rec[band];
    if (typeof pair !== 'object' || pair === null) return `Repas enfants ${CHILD_MEAL_BAND_LABELS_FR[band]} : manquant.`;
    for (const meal of ['breakfast', 'dinner'] as const) {
      const c = (pair as Record<string, unknown>)[meal];
      if (typeof c !== 'number' || !Number.isInteger(c) || c < 0) {
        return `Repas enfants ${CHILD_MEAL_BAND_LABELS_FR[band]} (${meal}) : entier ≥ 0 attendu (centimes).`;
      }
    }
  }
  return null;
}

// ── The set ──────────────────────────────────────────────────────────────────

/** Per-room-type numbers. Keyed by `RoomTypeId` so the set references real
 *  reference data rather than restating it.
 *
 *  `occupancy` is deliberately ABSENT: it is `pms_room_type.maxOccupancy`, real
 *  reference data about the building, not a pricing parameter. The line is
 *  worth holding — a parameter is something the operator may change to see what
 *  happens; the number of beds is not. */
export interface RoomTypeParameters {
  /** Above the season base, per season. Never a single global figure: Très
   *  Haute hikes only the two SUP types by €10 (brief §4). */
  markup: Record<RateSeason, Cents>;
  /** The authored « à partir de » shown on `apps/site`, and a free floor (R11).
   *  Null while unset — a posted price is authored, never derived. */
  minPosted: Cents | null;
}

/** Per-season numbers that are not per room type. */
export interface SeasonParameters {
  /** Double Standard, accommodation only — the single input the operator
   *  changes most (brief §4). Every other rate derives from it. */
  base: Cents;
  /** Minimum nights sellable in this season. 3 in Très Haute; 1 elsewhere. */
  minStay: number;
}

/**
 * THE object. Named, saved, compared, replayed, simulated.
 *
 * Note what is NOT here: any rate row. A set is what a grid is generated FROM,
 * and holding one would confuse the record with its projection.
 */
export interface RateParameterSet {
  /** Stable identity, unique per tenant — `2026`, `2027-draft-a`. This is what
   *  makes a booked night's provenance answerable by lookup rather than by
   *  judgement (« priced under 2026-v2 »), which is what R7 needs. */
  name: string;
  /** Free-text: why this set exists, what it changes. The field a diff cannot
   *  produce and a human always wants. */
  note: string | null;
  seasonCalendar: SeasonPeriod[];
  seasons: Record<RateSeason, SeasonParameters>;
  roomTypes: Record<RoomTypeId, RoomTypeParameters>;
  /** OTA-facing meal prices — the §2 grid and the OTA listing. */
  mealsBooking: MealPrices;
  /** Direct-engine meal prices (R12). */
  mealsDirect: MealPrices;
  /** Ordered ascending by `minNights`; the generator does not assume the order
   *  and sorts defensively, but a set that arrives unsorted is a smell. */
  lengthOfStayTiers: LengthOfStayTier[];
}

// ── Reading the calendar ─────────────────────────────────────────────────────

/**
 * Which season a date falls in, or `null` when the calendar does not cover it.
 *
 * Null is a real answer and must stay one: a calendar re-cut for a new year
 * legitimately stops before the next one is authored, and a generator that
 * silently defaulted to `basse` there would fill the grid with confident wrong
 * prices. The caller decides what an uncovered date means.
 *
 * Later periods win on overlap, so an operator can staple a correction onto the
 * end of a calendar without rewriting it. Overlap is still reported by
 * `validateRateParameterSet` — permitted, not encouraged.
 */
export function seasonOn(set: RateParameterSet, date: IsoDate): RateSeason | null {
  let found: RateSeason | null = null;
  for (const p of set.seasonCalendar) {
    if (date >= p.from && date <= p.to) found = p.season;
  }
  return found;
}

// ── The stored form ──────────────────────────────────────────────────────────

/** draft → published → archived (migration 0107).
 *
 *  A DRAFT is freely editable: it has priced nothing, so there is no history to
 *  preserve. A PUBLISHED set is FROZEN — parameters and name can never change
 *  and it cannot be deleted, only archived — because a night priced under it
 *  must stay explicable (Hard Rule 5, history preservation; R7). A correction is
 *  a NEW set, never an edit. */
export const RATE_PARAMETER_SET_STATUSES = ['draft', 'published', 'archived'] as const;
export type RateParameterSetStatus = (typeof RATE_PARAMETER_SET_STATUSES)[number];

export const RATE_PARAMETER_SET_STATUS_LABELS_FR: Record<RateParameterSetStatus, string> = {
  draft: 'Brouillon',
  published: 'Publié',
  archived: 'Archivé',
};

/** A set as it comes back from the database: the set itself, plus the identity
 *  and lifecycle the store gives it. `parameters` is the pure domain object —
 *  everything the generator needs and nothing about storage. */
export interface StoredRateParameterSet {
  id: number;
  status: RateParameterSetStatus;
  publishedAt: string | null;
  parameters: RateParameterSet;
}

